import API_CONFIG, { buildApiUrl, getEndpoint } from '../config/apiConfig';
import storageService from './storageService';

class TokenService {
  constructor() {
    this.accessToken = null;
    this.tokenExpiry = null;
    this.isRefreshing = false;
    this.refreshPromise = null;
  }

  // Store access token in memory
  setAccessToken(token, expiresIn = 3600) {
    this.accessToken = token;
    // Set expiry time (default 1 hour)
    this.tokenExpiry = Date.now() + (expiresIn * 1000);
  }

  // Get access token from memory
  getAccessToken() {
    // Check if token is expired
    if (this.tokenExpiry && Date.now() > this.tokenExpiry) {
      this.clearAccessToken();
      return null;
    }
    return this.accessToken;
  }

  // Clear access token from memory
  clearAccessToken() {
    this.accessToken = null;
    this.tokenExpiry = null;
  }

  // Check if access token is valid and not expired
  isAccessTokenValid() {
    return this.accessToken && this.tokenExpiry && Date.now() < this.tokenExpiry;
  }

  // Clear all tokens
  clearAllTokens() {
    this.clearAccessToken();
  }

  // Check if we have any valid authentication
  isAuthenticated() {
    return this.isAccessTokenValid();
  }

  // Get token for API requests (with automatic refresh if needed)
  async getValidAccessToken() {
    // If access token is valid, return it
    if (this.isAccessTokenValid()) {
      return this.accessToken;
    }

    // If already refreshing, wait for that to complete
    if (this.isRefreshing && this.refreshPromise) {
      try {
        await this.refreshPromise;
        return this.accessToken;
      } catch (error) {
        return null;
      }
    }

    // Try to refresh the token
    try {
      const newTokens = await this.refreshAccessToken();
      if (newTokens && newTokens.access_token) {
        return newTokens.access_token;
      }
    } catch (error) {
    }

    // No valid tokens available
    return null;
  }

  // Refresh access token using refresh token cookie
  async refreshAccessToken() {
    if (this.isRefreshing) {
      return this.refreshPromise;
    }

    this.isRefreshing = true;

    this.refreshPromise = (async () => {
      try {
        const formData = new FormData();
        formData.append('operation', 'RefreshAccessTokenForAddin');

        const response = await fetch(buildApiUrl(getEndpoint('OUTLOOK_API')), {
          method: 'POST',
          body: formData,
          credentials: 'include' // This will include the HTTP-only refresh token cookie
        });

        if (response.ok) {
          const data = await response.json();

          const result = data.result || data;
          const token = result.token || result.access_token || data.access_token || data.token;
          const expiresIn = result.expires_in || data.expires_in || 3600;

          if (data.success && token) {
            this.setAccessToken(token, expiresIn);

            try {
              const user = await storageService.getUser();
              if (user) {
                user.token = token;
                await storageService.saveUser(user);
              }

              const { default: authService } = await import('./authService');
              authService.updateCurrentUserToken(token);
            } catch (storageError) {
            }

            return { access_token: token, expires_in: expiresIn };
          } else {
            throw new Error(data.message || 'Token refresh failed');
          }
        } else {
          throw new Error(`Token refresh failed: ${response.status}`);
        }
      } catch (error) {
        // Clear tokens on refresh failure
        this.clearAllTokens();
        throw error;
      } finally {
        this.isRefreshing = false;
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  // Show token error notification
  showTokenErrorNotification(message) {
    // Token errors are now handled silently
  }
}

// Create singleton instance
const tokenService = new TokenService();

export default tokenService;